import {
  Clock,
  Mail,
  MapPin,
  Phone,
  Send,
  User,
  MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const Contact = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: <Phone className="h-5 w-5" />,
      title: t("contactPage.info.primaryPhone.title"),
      details: "+211 912 446 7697",
      link: "tel:+2119124467697",
      description: t("contactPage.info.primaryPhone.description"),
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    },
    {
      icon: <Phone className="h-5 w-5" />,
      title: t("contactPage.info.alternativePhone.title"),
      details: "+211 981 593 738",
      link: "tel:+211981593738",
      description: t("contactPage.info.alternativePhone.description"),
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: t("contactPage.info.primaryEmail.title"),
      details: "infoacdha@gmail.com",
      link: "mailto:infoacdha@gmail.com",
      description: t("contactPage.info.primaryEmail.description"),
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: t("contactPage.info.secondaryEmail.title"),
      details: "info@acdha.org",
      link: "mailto:info@acdha.org",
      description: t("contactPage.info.secondaryEmail.description"),
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200",
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      title: t("contactPage.info.officeAddress.title"),
      details: "Hai, Malakia Police Building",
      link: "#",
      description: t("contactPage.info.officeAddress.description"),
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
    },
  ];

  const officeHours = [
    {
      day: t("contactPage.hours.weekdays"),
      hours: "8:00 AM - 5:00 PM",
      status: "open",
    },
    {
      day: t("contactPage.hours.saturday"),
      hours: "9:00 AM - 2:00 PM",
      status: "open",
    },
    {
      day: t("contactPage.hours.sunday"),
      hours: t("contactPage.hours.closed"),
      status: "closed",
    },
  ];

  const contactPerson = {
    name: "Mr. Nouraldin Simon Paskawle",
    position: t("contactPage.contactPerson.position"),
    email: "infoacdha@gmail.com",
    phone: "+211 912 446 7697",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-green-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] sm:min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-green-800 via-green-700 to-green-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:py-24 text-center">
          <div className="mb-6 inline-flex items-center rounded-full bg-white/10 backdrop-blur-md px-4 py-2 border border-white/20">
            <MessageCircle className="mr-2 h-4 w-4 text-yellow-300" />
            <span className="text-sm font-medium text-white">Get in Touch</span>
          </div>

          <h1 className="mb-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
            {t("contactPage.hero.title")}
          </h1>
          <p className="mx-auto mb-8 max-w-3xl text-lg sm:text-xl text-green-100 leading-relaxed">
            {t("contactPage.hero.description")}
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="relative -mt-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {contactInfo.map((contact, index) => (
              <a
                key={contact.title}
                href={contact.link}
                className="group relative bg-white rounded-2xl p-6 shadow-lg border-2 border-transparent hover:border-green-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              >
                <div
                  className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${contact.color} rounded-t-2xl`}
                ></div>
                <div
                  className={`${contact.bgColor} w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <div
                    className={`text-white bg-gradient-to-r ${contact.color} p-2 rounded-lg`}
                  >
                    {contact.icon}
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-600 mb-1">
                  {contact.title}
                </h3>
                <p className="text-base font-bold text-gray-900 mb-1 group-hover:text-green-700 transition-colors">
                  {contact.details}
                </p>
                <p className="text-xs text-gray-500">{contact.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form - Left Side */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
                <div className="mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-r from-green-600 to-green-700 mb-4">
                    <Send className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    {t("contactPage.form.title")}
                  </h2>
                  <p className="text-gray-600">
                    {t("contactPage.form.subtitle")}
                  </p>
                </div>

                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        {t("contactPage.form.fields.name")} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3.5 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all outline-none bg-gray-50 focus:bg-white"
                        placeholder={t("contactPage.form.placeholders.name")}
                      />
                    </div>
                    <div className="relative">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        {t("contactPage.form.fields.email")} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3.5 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all outline-none bg-gray-50 focus:bg-white"
                        placeholder={t("contactPage.form.placeholders.email")}
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      {t("contactPage.form.fields.phone")}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3.5 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder={t("contactPage.form.placeholders.phone")}
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="subject"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      {t("contactPage.form.fields.subject")} *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3.5 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder={t("contactPage.form.placeholders.subject")}
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      {t("contactPage.form.fields.message")} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      className="w-full resize-none rounded-xl border-2 border-gray-200 px-4 py-3.5 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder={t("contactPage.form.placeholders.message")}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full group inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-8 py-4 font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:from-green-700 hover:to-green-800"
                  >
                    <Send className="mr-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    {t("contactPage.form.submit")}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar - Right Side */}
            <div className="space-y-6">
              {/* Contact Person Card */}
              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-3xl p-8 text-white shadow-xl">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mr-4">
                    <User className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {t("contactPage.contactPerson.title")}
                    </h3>
                    <p className="text-green-100 text-sm">
                      {t("contactPage.contactPerson.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-green-100 mb-1">
                      {t("contactPage.contactPerson.name")}
                    </p>
                    <p className="font-semibold">{contactPerson.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-100 mb-1">
                      {t("contactPage.contactPerson.positionLabel")}
                    </p>
                    <p className="font-semibold">{contactPerson.position}</p>
                  </div>
                  <div className="pt-4 border-t border-white/20">
                    <p className="text-sm text-green-100 mb-2">
                      {t("contactPage.contactPerson.contact")}
                    </p>
                    <div className="space-y-2">
                      <a
                        href={`mailto:${contactPerson.email}`}
                        className="flex items-center text-sm hover:underline"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        {contactPerson.email}
                      </a>
                      <a
                        href={`tel:${contactPerson.phone.replace(/\s/g, "")}`}
                        className="flex items-center text-sm hover:underline"
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        {contactPerson.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Hours Card */}
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-600 to-green-700 flex items-center justify-center mr-4">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t("contactPage.officeHours.title")}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  {t("contactPage.officeHours.subtitle")}
                </p>
                <div className="space-y-3">
                  {officeHours.map((schedule) => (
                    <div
                      key={schedule.day}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                    >
                      <span className="font-semibold text-gray-900">
                        {schedule.day}
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          schedule.status === "closed"
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-br from-green-800 via-green-700 to-green-900 px-4 py-20 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-4xl md:text-5xl font-bold text-white">
            {t("contactPage.cta.title")}
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-xl text-green-100">
            {t("contactPage.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/get-involved"
              className="inline-flex items-center justify-center rounded-xl bg-yellow-500 px-8 py-4 text-lg font-semibold text-green-900 shadow-lg hover:bg-yellow-400 transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              {t("contactPage.cta.getInvolved")}
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center rounded-xl border-2 border-white bg-transparent px-8 py-4 text-lg font-semibold text-white hover:bg-white/10 transition-all duration-300"
            >
              {t("contactPage.cta.learnMore")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
